import { addDays, format } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import CheckboxField from 'components/forms/checkboxField/CheckboxField';
import FormWithProvider from 'components/forms/form/Form';
import Button from 'components/button/Button';
import Loader from 'components/loader/Loader';
import useFormatCurrency from 'hooks/useFormatCurrency';
import { TransLink } from 'components/text/TransLink';
import { useScreenAndProtectContext } from '../ScreenAndProtect';
import DisplayValue from '../common/displayValue/DisplayValue';
import {
  ScreenAndProtectContainer,
  ScreenAndProtectHero,
  ScreenAndProtectSection,
  ScreenAndProtectSectionsContainer,
} from '../common/ScreenAndProtectCommon.styles';
import useFetchProperties from '../common/useFetchProperties';
import useEnrollScreenAndProtect from '../useEnrollScreenAndProtect';
import {
  Disclaimer,
  Divider,
  ReviewDetailsHeader,
} from './ScreenAndProtectDetails.styles';
import {
  ScreenAndProtectDetailsFormSchemaType,
  screenAndProtectDetailsSchema,
} from './ScreenAndProtectDetails.schema';
import ConfirmProtectionButton from './ConfirmProtectionButton';
import PropertiesSelect from './propertiesSelect/PropertiesSelect';
import ReservationDetails from './ReservationDetails';

const ScreenAndProtectDetails = () => {
  const { allProperties, isFetching } = useFetchProperties();
  const { isEnrolled } = useScreenAndProtectContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enroll } = useEnrollScreenAndProtect();
  const formatCurrency = useFormatCurrency();

  if (isEnrolled) {
    return <Navigate to="/screen-and-protect/current" replace />;
  }

  if (!allProperties && isFetching) {
    return <Loader />;
  }

  const defaultValues = {
    selectedProperties: allProperties?.map((property) => property.uid) ?? [],
    reservationType: 'new',
    startDate: addDays(new Date(), 2),
  };

  const handleSubmit = async (
    values: ScreenAndProtectDetailsFormSchemaType,
  ) => {
    // double safe-guard:
    if (!values.agreeToTerms || !values.agreeToPrivacy) {
      return;
    }

    await enroll({
      propertiesUids: values.selectedProperties as string[],
      includeFutureBookings: values.reservationType === 'all',
      startDate: format(values.startDate, 'yyyy-MM-dd'),
    });
  };

  return (
    <ScreenAndProtectContainer
      className="container"
      data-testid="screen-and-protect-details"
    >
      <ScreenAndProtectHero>
        <Button onClick={() => navigate('/screen-and-protect')}>
          {t('common.back')}
        </Button>
        <h1>{t('pageScreenAndProtect.detailsPage.hero.title')}</h1>
        <p>{t('pageScreenAndProtect.detailsPage.hero.subtitle')}</p>
      </ScreenAndProtectHero>

      <Divider />

      <ReviewDetailsHeader>
        <span>
          {t('pageScreenAndProtect.detailsPage.damageCoverage.label')}
        </span>
        <h2>{t('pageScreenAndProtect.detailsPage.damageCoverage.title')}</h2>
      </ReviewDetailsHeader>

      <ScreenAndProtectSectionsContainer>
        <ScreenAndProtectSection data-testid="screen-and-protect-cost">
          <h3>
            {t(
              'pageScreenAndProtect.detailsPage.sections.screenAndProtectCost.title',
            )}
          </h3>

          <DisplayValue
            label={t(
              'pageScreenAndProtect.detailsPage.sections.screenAndProtectCost.approvedOrFlaggedBookings',
            )}
            value={
              <span>
                {t(
                  'pageScreenAndProtect.detailsPage.sections.screenAndProtectCost.costApprovedOrFlagged',
                  {
                    cost: formatCurrency({
                      value: 8.5,
                      currency: 'USD',
                      options: { maximumFractionDigits: 1 },
                    }),
                  },
                )}
              </span>
            }
            valueSize="large"
            valueDataIntercomTarget="details-screen-protect-approved-booking-cost"
          />

          <DisplayValue
            label={t(
              'pageScreenAndProtect.detailsPage.sections.screenAndProtectCost.approvedOrFlaggedBookingsPlusNights',
            )}
            value={
              <span>
                {t(
                  'pageScreenAndProtect.detailsPage.sections.screenAndProtectCost.costApprovedOrFlagged',
                  { cost: formatCurrency({ value: 5, currency: 'USD' }) },
                )}
              </span>
            }
            valueSize="large"
            valueDataIntercomTarget="details-screen-protect-approved-booking-31-plus-cost"
          />

          <DisplayValue
            label={t(
              'pageScreenAndProtect.detailsPage.sections.screenAndProtectCost.rejectedOrCancelledBookings',
            )}
            value={
              <span>
                {t(
                  'pageScreenAndProtect.detailsPage.sections.screenAndProtectCost.costRejectedOrCancelled',
                  { cost: formatCurrency({ value: 1, currency: 'USD' }) },
                )}
              </span>
            }
            valueSize="large"
            valueDataIntercomTarget="details-screen-protect-rejected-booking-cost"
          />
        </ScreenAndProtectSection>

        <ScreenAndProtectSection data-testid="maximum-protection-payment">
          <h3>
            {t(
              'pageScreenAndProtect.detailsPage.sections.maximumProtectionPayment.title',
            )}
          </h3>
          <DisplayValue
            label={t(
              'pageScreenAndProtect.detailsPage.sections.maximumProtectionPayment.approvedBooking',
            )}
            value={
              <span>
                {t(
                  'pageScreenAndProtect.detailsPage.sections.maximumProtectionPayment.maxApprovedPayment',
                  {
                    maxPayment: formatCurrency({
                      value: 50000,
                      currency: 'USD',
                    }),
                  },
                )}
              </span>
            }
            valueSize="large"
            valueDataIntercomTarget="details-screen-protect-approved-coverage"
          />
          <DisplayValue
            label={t(
              'pageScreenAndProtect.detailsPage.sections.maximumProtectionPayment.flaggedBooking',
            )}
            value={
              <span>
                {t(
                  'pageScreenAndProtect.detailsPage.sections.maximumProtectionPayment.maxFlaggedPayment',
                  {
                    maxPayment: formatCurrency({
                      value: 1000,
                      currency: 'USD',
                    }),
                  },
                )}
              </span>
            }
            valueSize="large"
            valueDataIntercomTarget="details-screen-protect-flagged-coverage"
          />
        </ScreenAndProtectSection>

        <FormWithProvider
          onSubmit={handleSubmit}
          schema={screenAndProtectDetailsSchema(t)}
          defaultValues={defaultValues}
        >
          <ScreenAndProtectSection data-testid="protection-details">
            <h3>
              {t(
                'pageScreenAndProtect.detailsPage.sections.protectionDetails.title',
              )}
            </h3>

            <DisplayValue
              label={t(
                'pageScreenAndProtect.detailsPage.sections.protectionDetails.bookingChannels',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.detailsPage.sections.protectionDetails.bookingChannelsValue',
                  )}
                </span>
              }
              valueDataIntercomTarget="details-screen-protect-booking-channels"
            />

            <DisplayValue
              label={t(
                'pageScreenAndProtect.detailsPage.sections.protectionDetails.properties',
              )}
              value={<PropertiesSelect />}
            />

            <ReservationDetails />
          </ScreenAndProtectSection>

          <CheckboxField name="agreeToTerms" required>
            <Trans
              i18nKey="pageScreenAndProtect.detailsPage.agreements.terms"
              components={{
                link1: (
                  <TransLink
                    href="https://truvi.com/screen-and-protect/host-protection/"
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </CheckboxField>
          <CheckboxField name="agreeToPrivacy" required>
            <Trans
              i18nKey="pageScreenAndProtect.detailsPage.agreements.privacy"
              components={{
                link1: (
                  <TransLink
                    href="https://truvi.com/privacy-policy/"
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </CheckboxField>

          <ConfirmProtectionButton />
        </FormWithProvider>
      </ScreenAndProtectSectionsContainer>

      <Disclaimer>
        <p>{t('pageScreenAndProtect.detailsPage.disclaimer.body')}</p>
      </Disclaimer>
    </ScreenAndProtectContainer>
  );
};

export default ScreenAndProtectDetails;
